import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from './CartContext';
import { useCurrency } from './CurrencyContext';
import { getPriceInCurrency } from './currencyHelpers';
import { fetchRentalProducts } from './rentalApi';
import {
  Box, Typography, Container, Grid, Card, CardMedia, CardContent, CardActions,
  Chip, Button, IconButton, Skeleton, TextField, InputAdornment, Stack,
  alpha, useTheme, useMediaQuery, Fade, Grow, Slide, Badge, Tooltip,
  Dialog, DialogContent, Divider, Rating, Avatar, Paper, ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import InventoryIcon from '@mui/icons-material/Inventory';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SecurityIcon from '@mui/icons-material/Security';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import TuneIcon from '@mui/icons-material/Tune';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

// ═══════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4); }
  50% { box-shadow: 0 0 0 12px rgba(25, 118, 210, 0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
`;

// ═══════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════

const PRICE_TYPE_CONFIG = {
  hourly: { icon: '⏰', labelAr: 'بالساعة', labelEn: 'Per Hour', color: '#FF6B6B' },
  daily: { icon: '📅', labelAr: 'باليوم', labelEn: 'Per Day', color: '#4ECDC4' },
  weekly: { icon: '📆', labelAr: 'بالأسبوع', labelEn: 'Per Week', color: '#45B7D1' },
  monthly: { icon: '🗓️', labelAr: 'بالشهر', labelEn: 'Per Month', color: '#96CEB4' },
};

const getImageUrl = (imageData) => {
  if (!imageData) return null;
  if (typeof imageData === 'string') {
    try {
      const parsed = JSON.parse(imageData);
      return parsed.medium || parsed.large || parsed.thumbnail || imageData;
    } catch { return imageData; }
  }
  if (typeof imageData === 'object') return imageData.medium || imageData.large || imageData.thumbnail;
  return null;
};

// ═══════════════════════════════════════════
// HERO SECTION
// ═══════════════════════════════════════════

const RentalHero = ({ isAr, productCount }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: { xs: 0, sm: 4 },
        mb: { xs: 3, sm: 4 },
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, #00BCD4 100%)`,
        minHeight: { xs: 200, sm: 240, md: 280 },
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {/* Decorative circles */}
      <Box sx={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', bgcolor: alpha('#fff', 0.05) }} />
      <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', bgcolor: alpha('#fff', 0.08) }} />
      <Box sx={{ position: 'absolute', top: '50%', right: '15%', width: 80, height: 80, borderRadius: '50%', bgcolor: alpha('#fff', 0.04), animation: `${float} 4s ease-in-out infinite` }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="space-between" spacing={3} sx={{ py: { xs: 4, sm: 5, md: 6 }, px: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: { xs: 'center', sm: isAr ? 'right' : 'left' } }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'center', sm: 'flex-start' }} sx={{ mb: 2 }}>
              <Chip
                label={isAr ? 'خدمة التأجير' : 'Rental Service'}
                sx={{
                  bgcolor: alpha('#fff', 0.15),
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  backdropFilter: 'blur(10px)',
                  '& .MuiChip-icon': { color: '#fff' }
                }}
              />
              {productCount > 0 && (
                <Chip
                  label={` ${isAr ? 'المنتجات' : 'items'} : ${productCount}`}
                  sx={{ bgcolor: alpha('#fff', 0.1), color: '#fff', fontSize: '0.75rem' }}
                />
              )}
            </Stack>

            <Typography
              variant="h3"
              sx={{
                color: '#fff',
                fontWeight: 900,
                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' },
                lineHeight: 1.2,
                mb: 1.5,
                textShadow: '0 4px 20px rgba(0,0,0,0.2)',
              }}
            >
              {isAr ? 'استأجر ما تحتاجه' : 'Rent What You Need'}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: alpha('#fff', 0.85),
                fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                maxWidth: 460,
                lineHeight: 1.6,
              }}
            >
              {isAr
                ? 'اكتشف مجموعة واسعة من المنتجات المتاحة للإيجار بأسعار مناسبة'
                : 'Discover a wide range of products available for rent at great prices'}
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mt: 3 }} justifyContent={{ xs: 'center', sm: 'flex-start' }}>
              {[
                { icon: <VerifiedIcon sx={{ fontSize: 18 }} />, text: isAr ? 'منتجات مضمونة' : 'Verified Items' },
                { icon: <SecurityIcon sx={{ fontSize: 18 }} />, text: isAr ? 'تأجير آمن' : 'Secure Rental' },
              ].map((item, i) => (
                <Stack key={i} direction="row" spacing={0.5} alignItems="center" sx={{ color: alpha('#fff', 0.9) }}>
                  {item.icon}
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>{item.text}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          {!isMobile && (
            <Box sx={{ animation: `${float} 5s ease-in-out infinite` }}>
              <HomeRepairServiceIcon sx={{ fontSize: { sm: 120, md: 160 }, color: alpha('#fff', 0.15) }} />
            </Box>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

// ═══════════════════════════════════════════
// PRODUCT CARD
// ═══════════════════════════════════════════

const RentalProductCard = ({ product, index, isAr, onSelect, viewMode }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { selectedCurrency, getCurrencySymbol } = useCurrency();
  const currencySymbol = getCurrencySymbol(selectedCurrency);

  const priceType = PRICE_TYPE_CONFIG[product.RentalPriceType] || PRICE_TYPE_CONFIG.daily;
  const imageUrl = getImageUrl(product.ImageUrl) || getImageUrl(product.ImageJson);

  const rentalPrice = product.RentalCurrencyPrices?.[selectedCurrency]
    || product.RentalCurrencyPrices?.YER
    || product.RentalPrice
    || 0;

  const depositPrice = product.RentalDepositCurrencyPrices?.[selectedCurrency]
    || product.RentalDepositCurrencyPrices?.YER
    || 0;

  const isAvailable = product.IsActive !== false && (product.RentalStock === null || product.RentalStock > 0);

  if (viewMode === 'list') {
    return (
      <Grow in timeout={300 + index * 80}>
        <Card
          onClick={() => isAvailable && onSelect(product)}
          sx={{
            display: 'flex',
            borderRadius: 3,
            overflow: 'hidden',
            cursor: isAvailable ? 'pointer' : 'default',
            opacity: isAvailable ? 1 : 0.6,
            transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
            border: '1px solid',
            borderColor: alpha(theme.palette.divider, 0.08),
            boxShadow: `0 2px 12px ${alpha('#000', 0.06)}`,
            '&:hover': isAvailable ? {
              transform: 'translateX(4px)',
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              borderColor: alpha(theme.palette.primary.main, 0.2),
            } : {},
            mb: 2,
          }}
        >
          <Box sx={{ position: 'relative', width: { xs: 120, sm: 160, md: 200 }, flexShrink: 0 }}>
            <CardMedia component="img" image={imageUrl || '/placeholder.png'} alt={product.ProductName}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', minHeight: { xs: 120, sm: 140 } }} />
            <Chip
              label={`${priceType.icon} ${isAr ? priceType.labelAr : priceType.labelEn}`}
              size="small"
              sx={{
                position: 'absolute', top: 8, left: 8,
                bgcolor: alpha(priceType.color, 0.9), color: '#fff',
                fontWeight: 700, fontSize: '0.65rem', backdropFilter: 'blur(8px)',
              }}
            />
            {!isAvailable && (
              <Box sx={{ position: 'absolute', inset: 0, bgcolor: alpha('#000', 0.5), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Chip label={isAr ? 'غير متاح' : 'Unavailable'} color="error" size="small" sx={{ fontWeight: 700 }} />
              </Box>
            )}
          </Box>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: { xs: 1.5, sm: 2 } }}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {product.ProductName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' }, mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {product.Description || (isAr ? 'لا يوجد وصف' : 'No description')}
            </Typography>
            <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="h6" color="primary.main" fontWeight={900} sx={{ fontSize: { xs: '1rem', sm: '1.2rem' }, lineHeight: 1 }}>
                  {rentalPrice.toLocaleString()} {currencySymbol}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  / {isAr ? priceType.labelAr : priceType.labelEn}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5}>
                {product.RentalStock != null && (
                  <Chip icon={<InventoryIcon sx={{ fontSize: 12 }} />} label={`${product.RentalStock}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 24 }} />
                )}
                {depositPrice > 0 && (
                  <Chip icon={<SecurityIcon sx={{ fontSize: 12 }} />} label={`${isAr ? 'تأمين' : 'Dep'}: ${depositPrice.toLocaleString()}`} size="small" color="warning" variant="outlined" sx={{ fontSize: '0.6rem', height: 24 }} />
                )}
              </Stack>
            </Box>
          </Box>
        </Card>
      </Grow>
    );
  }

  // Grid view
  return (
    <Grow in timeout={300 + index * 100}>
      <Card
        onClick={() => isAvailable && onSelect(product)}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: { xs: 1, sm: 1.5 },
          overflow: 'hidden',
          cursor: isAvailable ? 'pointer' : 'default',
          opacity: isAvailable ? 1 : 0.6,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          border: '1px solid',
          borderColor: alpha(theme.palette.divider, 0.06),
          boxShadow: `0 4px 20px ${alpha('#000', 0.04)}`,
          '&:hover': isAvailable ? {
            transform: isMobile ? 'scale(1.02)' : 'translateY(-8px)',
            boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.15)}`,
            borderColor: alpha(theme.palette.primary.main, 0.15),
            '& .product-image': { transform: 'scale(1.08)' },
            '& .rent-btn': { opacity: 1, transform: 'translateY(0)' },
          } : {},
        }}
      >
        <Box sx={{ position: 'relative', overflow: 'hidden', pt: { xs: '60%', sm: '65%' } }}>
          <CardMedia
            className="product-image"
            component="img"
            image={imageUrl || '/placeholder.png'}
            alt={product.ProductName}
            sx={{
              position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
              objectFit: 'cover', transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          />

          {/* Top badges */}
          <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', top: { xs: 7, sm: 10 }, left: { xs: 7, sm: 10 }, right: { xs: 7, sm: 10 } }}>
            <Chip
              label={`${priceType.icon} ${isAr ? priceType.labelAr : priceType.labelEn}`}
              size="small"
              sx={{ bgcolor: alpha(priceType.color, 0.9), color: '#fff', fontWeight: 700, fontSize: { xs: '0.55rem', sm: '0.7rem' }, backdropFilter: 'blur(8px)', height: { xs: 20, sm: 26 } }}
            />
            {product.RentalStock != null && product.RentalStock <= 3 && product.RentalStock > 0 && (
              <Chip
                label={`🔥 ${product.RentalStock} ${isAr ? 'فقط' : 'left'}`}
                size="small"
                sx={{ bgcolor: alpha('#FF5722', 0.9), color: '#fff', fontWeight: 700, fontSize: { xs: '0.55rem', sm: '0.6rem' }, height: { xs: 20, sm: 26 } }}
              />
            )}
          </Stack>

          {/* Unavailable overlay */}
          {!isAvailable && (
            <Box sx={{ position: 'absolute', inset: 0, bgcolor: alpha('#000', 0.55), display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' }}>
              <Chip label={isAr ? 'غير متاح حالياً' : 'Currently Unavailable'} color="error" sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', sm: '0.8rem' } }} />
            </Box>
          )}

          {/* Hover rent button */}
          {isAvailable && !isMobile && (
            <Box className="rent-btn" sx={{
              position: 'absolute', bottom: 12, left: 12, right: 12,
              opacity: 0, transform: 'translateY(10px)', transition: 'all 0.3s ease',
            }}>
              <Button fullWidth variant="contained" size="medium" sx={{
                borderRadius: 3, fontWeight: 800, fontSize: '0.85rem', py: 1,
                backdropFilter: 'blur(10px)',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
              }}>
                {isAr ? 'استأجر الآن' : 'Rent Now'}
              </Button>
            </Box>
          )}
        </Box>

        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: { xs: 1.2, sm: 2 } }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{
            fontSize: { xs: '0.78rem', sm: '0.95rem' }, mb: 0.5,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {product.ProductName}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{
            fontSize: { xs: '0.65rem', sm: '0.8rem' }, mb: { xs: 1, sm: 1.5 }, lineHeight: 1.5,
            overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {product.Description || (isAr ? 'لا يوجد وصف' : 'No description')}
          </Typography>

          <Box sx={{ mt: 'auto' }}>
            {/* Duration info */}
            <Stack direction="row" spacing={0.5} sx={{ mb: { xs: 0.6, sm: 1 }, flexWrap: 'wrap', gap: { xs: 0.3, sm: 0.5 } }}>
              <Chip
                icon={<AccessTimeIcon sx={{ fontSize: { xs: 10, sm: 12 } }} />}
                label={`${isAr ? 'الحد الأدنى' : 'Min'}: ${product.RentalMinDuration || 1}`}
                size="small" variant="outlined"
                sx={{ fontSize: { xs: '0.52rem', sm: '0.6rem' }, height: { xs: 18, sm: 22 } }}
              />
              {product.RentalMaxDuration && (
                <Chip
                  label={`${isAr ? 'الحد الأقصى' : 'Max'}: ${product.RentalMaxDuration}`}
                  size="small" variant="outlined"
                  sx={{ fontSize: { xs: '0.52rem', sm: '0.6rem' }, height: { xs: 18, sm: 22 } }}
                />
              )}
            </Stack>

            {/* Price */}
            <Box sx={{
              display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderRadius: { xs: 1.5, sm: 2 }, p: { xs: 0.8, sm: 1.5 },
            }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.5rem', sm: '0.6rem' }, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {isAr ? 'سعر الإيجار' : 'Rental Price'}
                </Typography>
                <Typography variant="h6" color="primary.main" fontWeight={900} sx={{ fontSize: { xs: '0.95rem', sm: '1.3rem' }, lineHeight: 1.1 }}>
                  {rentalPrice.toLocaleString()} <Typography component="span" sx={{ fontSize: { xs: '0.55rem', sm: '0.7rem' }, fontWeight: 600 }}>{currencySymbol}</Typography>
                </Typography>
              </Box>
              {depositPrice > 0 && (
                <Tooltip title={isAr ? 'مبلغ التأمين (مسترد)' : 'Security Deposit (Refundable)'} arrow>
                  <Chip
                    icon={<SecurityIcon sx={{ fontSize: { xs: 10, sm: 12 } }} />}
                    label={`${depositPrice.toLocaleString()} ${currencySymbol}`}
                    size="small" color="warning" variant="outlined"
                    sx={{ fontSize: { xs: '0.5rem', sm: '0.6rem' }, height: { xs: 18, sm: 22 } }}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
        </CardContent>

        {/* Mobile rent button */}
        {isAvailable && isMobile && (
          <Box sx={{ px: 1.2, pb: 1.2 }}>
            <Button fullWidth variant="contained" size="small" sx={{
              borderRadius: 2, fontWeight: 800, fontSize: '0.72rem', py: 0.6,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            }}>
              {isAr ? 'استأجر الآن' : 'Rent Now'}
            </Button>
          </Box>
        )}
      </Card>
    </Grow>
  );
};

// ═══════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════

export default function RentalsPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { selectedBranch } = useCart();
  const { selectedCurrency } = useCurrency();
  const isAr = i18n.language === 'ar';

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [filterType, setFilterType] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);

  const branchId = selectedBranch?.id || selectedBranch?.Id;

  useEffect(() => {
    const load = async () => {
      if (!branchId) return;
      setLoading(true);
      try {
        const data = await fetchRentalProducts(branchId, selectedCurrency);
        setProducts(data || []);
      } catch (err) {
        console.error('Error loading rentals:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [branchId, selectedCurrency]);

  const filtered = products.filter(p => {
    const matchSearch = p.ProductName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.Description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = filterType === 'all' || p.RentalPriceType === filterType;
    return matchSearch && matchType;
  });

  const handleSelectProduct = (product) => {
    navigate(`/rentals/${product.Id}`);
  };

  return (
    <Box sx={{ minHeight: '80vh', bgcolor: 'background.default', pb: { xs: 10, sm: 6 } }}>
      {/* Hero */}
      <RentalHero isAr={isAr} productCount={products.length} />

      <Container maxWidth="lg" sx={{ px: { xs: 0.5, sm: 2, md: 3 } }}>
        {/* Search & Filters */}
        <Paper elevation={0} sx={{
          p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 3,
          border: '1px solid', borderColor: alpha(theme.palette.divider, 0.08),
          boxShadow: `0 2px 12px ${alpha('#000', 0.04)}`,
        }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              fullWidth size="small" placeholder={isAr ? 'ابحث عن منتجات للإيجار...' : 'Search rental products...'}
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary' }} /></InputAdornment>,
                sx: { borderRadius: 3, bgcolor: alpha(theme.palette.background.default, 0.6) }
              }}
              sx={{ maxWidth: { sm: 400 } }}
            />

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <ToggleButtonGroup
                value={filterType} exclusive
                onChange={(_, v) => v && setFilterType(v)}
                size="small"
                sx={{ '& .MuiToggleButton-root': { borderRadius: '20px !important', px: { xs: 1.5, sm: 2 }, fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600, textTransform: 'none', border: 'none', mx: 0.3, bgcolor: alpha(theme.palette.action.selected, 0.04), '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } } } }}
              >
                <ToggleButton value="all">{isAr ? 'الكل' : 'All'}</ToggleButton>
                {Object.entries(PRICE_TYPE_CONFIG).map(([key, cfg]) => (
                  <ToggleButton key={key} value={key}>{cfg.icon} {isAr ? cfg.labelAr : cfg.labelEn}</ToggleButton>
                ))}
              </ToggleButtonGroup>

              <ToggleButtonGroup
                value={viewMode} exclusive
                onChange={(_, v) => v && setViewMode(v)}
                size="small"
                sx={{ ml: 'auto', '& .MuiToggleButton-root': { px: 1 } }}
              >
                <ToggleButton value="grid"><ViewModuleIcon sx={{ fontSize: 20 }} /></ToggleButton>
                <ToggleButton value="list"><ViewListIcon sx={{ fontSize: 20 }} /></ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Stack>
        </Paper>

        {/* Results count */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
            {isAr ? ` المنتجات المتاحة : ${filtered.length} ` : `${filtered.length} products available`}
          </Typography>
        </Stack>

        {/* Loading Skeletons */}
        {loading ? (
          <Grid container spacing={{ xs: 0.5, sm: 2, md: 3 }}>            {[...Array(6)].map((_, i) => (
              <Grid item xs={6} sm={4} md={3} key={i}>
                <Skeleton variant="rounded" height={320} sx={{ borderRadius: 4 }} />
              </Grid>
            ))}
          </Grid>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 10 } }}>
            <HomeRepairServiceIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: alpha(theme.palette.text.secondary, 0.2), mb: 2 }} />
            <Typography variant="h6" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.2rem' }, mb: 1 }}>
              {isAr ? 'لا توجد منتجات للإيجار' : 'No rental products found'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
              {searchTerm ? (isAr ? 'جرب كلمات بحث مختلفة' : 'Try different search terms') : (isAr ? 'سيتم إضافة منتجات قريباً' : 'Products coming soon')}
            </Typography>
          </Box>
        ) : viewMode === 'grid' ? (
          <Grid container spacing={{ xs: 0.5, sm: 2, md: 3 }}>            {filtered.map((product, i) => (
              <Grid item xs={6} sm={4} md={3} key={product.Id}>
                <RentalProductCard product={product} index={i} isAr={isAr} onSelect={handleSelectProduct} viewMode="grid" />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box>
            {filtered.map((product, i) => (
              <RentalProductCard key={product.Id} product={product} index={i} isAr={isAr} onSelect={handleSelectProduct} viewMode="list" />
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
}